import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Loader from '../../components/Loader';
import { toast } from 'react-toastify';
import ApiService from '../../services/ApiService';
import { Tab, Tabs } from 'react-bootstrap';
import { formatDate, formatCurrency, calculateAge } from '../../utils/helper';
import { getGenericStatus, getAppointmentStatus } from '../../utils/statusHelper';

const Details = () => {
    const { id } = useParams();
    const [isLoader, setIsLoader] = useState(false);
    const [patient, setPatient] = useState(null);
    const [appointments, setAppointments] = useState([]);
    const [orders, setOrders] = useState([]);
    const [key, setKey] = useState('info');
    const [appointmentType, setAppointmentType] = useState('doctor');

    useEffect(() => {
        getPatientData();
    }, [id]);

    const getPatientData = async () => {
        setIsLoader(true);
        try {
            // Fetch Patient Basic Info
            const patientRes = await ApiService.request({
                method: 'GET',
                url: `admin/patients/${id}`,
            });

            if (patientRes.data.status) {
                setPatient(patientRes.data.data);
            } else {
                toast.error(patientRes.data.message);
            }

            // Fetch Appointments
            const apptRes = await ApiService.request({
                method: 'GET',
                url: 'admin/appointments',
                data: { patient_id: id, per_page: 100 }
            });
            if (apptRes.data.status) {
                setAppointments(apptRes.data.data.appointments || []);
            }

            // Fetch Orders
            const orderRes = await ApiService.request({
                method: 'GET',
                url: 'admin/orders',
                data: { patient_id: id, per_page: 100 }
            });
            if (orderRes.data.status) {
                setOrders(orderRes.data.data.orders || []);
            }

        } catch (error) {
            console.error(error);
            toast.error('Failed to load patient data');
        } finally {
            setIsLoader(false);
        }
    };

    if (isLoader) return <Loader />;
    if (!patient) return <div className="text-center p-5">Patient not found</div>;

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                            Patient Details
                        </h1>
                        <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                            <li className="breadcrumb-item text-muted">
                                <Link to="/patients" className="text-muted text-hover-primary">Patients</Link>
                            </li>
                            <li className="breadcrumb-item">
                                <span className="bullet bg-gray-400 w-5px h-2px"></span>
                            </li>
                            <li className="breadcrumb-item text-muted">{patient.name}</li>
                        </ul>
                    </div>
                    <div className="d-flex align-items-center gap-2 gap-lg-3">
                        <Link to="/patients" className="btn btn-sm fw-bold btn-secondary">Back</Link>
                        <Link to={`/patients/edit/${id}`} className="btn btn-sm fw-bold btn-primary">Edit Patient</Link>
                    </div>
                </div>
            </div>

            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-fluid">
                    <div className="card mb-5 mb-xl-10">
                        <div className="card-body pt-9 pb-0">
                            <div className="d-flex flex-wrap flex-sm-nowrap mb-3">
                                <div className="me-7 mb-4">
                                    <div className="symbol symbol-100px symbol-lg-160px symbol-fixed position-relative">
                                        <img src={patient.picture || "/assets/images/blank.png"} alt={patient.name} />
                                    </div>
                                </div>
                                <div className="flex-grow-1">
                                    <div className="d-flex justify-content-between align-items-start flex-wrap mb-2">
                                        <div className="d-flex flex-column">
                                            <div className="d-flex align-items-center mb-2">
                                                <span className="text-gray-900 text-hover-primary fs-2 fw-bold me-1">{patient.name}</span>
                                            </div>
                                            <div className="d-flex flex-wrap fw-semibold fs-6 mb-4 pe-2">
                                                <span className="d-flex align-items-center text-gray-400 me-5 mb-2">
                                                    <i className="fas fa-phone me-1"></i> {patient.phone}
                                                </span>
                                                <span className="d-flex align-items-center text-gray-400 me-5 mb-2">
                                                    <i className="fas fa-envelope me-1"></i> {patient.email || 'N/A'}
                                                </span>
                                                <span className="d-flex align-items-center text-gray-400 mb-2">
                                                    <i className="fas fa-map-marker-alt me-1"></i> {patient.address || 'N/A'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <Tabs
                                id="patient-tabs"
                                activeKey={key}
                                onSelect={(k) => setKey(k)}
                                className="nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-5 fw-bold"
                            >
                                <Tab eventKey="info" title="Overview">
                                    <div className="py-5">
                                        <div className="row mb-7">
                                            <label className="col-lg-4 fw-semibold text-muted">Full Name</label>
                                            <div className="col-lg-8">
                                                <span className="fw-bold fs-6 text-gray-800">{patient.name}</span>
                                            </div>
                                        </div>
                                        <div className="row mb-7">
                                            <label className="col-lg-4 fw-semibold text-muted">Phone</label>
                                            <div className="col-lg-8">
                                                <span className="fw-bold fs-6 text-gray-800">{patient.phone}</span>
                                            </div>
                                        </div>
                                        <div className="row mb-7">
                                            <label className="col-lg-4 fw-semibold text-muted">Email</label>
                                            <div className="col-lg-8">
                                                <span className="fw-bold fs-6 text-gray-800">{patient.email || '-'}</span>
                                            </div>
                                        </div>
                                        <div className="row mb-7">
                                            <label className="col-lg-4 fw-semibold text-muted">Gender</label>
                                            <div className="col-lg-8">
                                                <span className="fw-bold fs-6 text-gray-800 text-capitalize">{patient.gender}</span>
                                            </div>
                                        </div>
                                        <div className="row mb-7">
                                            <label className="col-lg-4 fw-semibold text-muted">Insurance Type</label>
                                            <div className="col-lg-8">
                                                <span className="fw-bold fs-6 text-gray-800">
                                                    {patient.insurance_type?.title || '-'}
                                                </span>
                                            </div>
                                        </div>
                                        {patient.insurance_type && (
                                            <div className="row mb-7">
                                                <label className="col-lg-4 fw-semibold text-muted">Insurance Card Number</label>
                                                <div className="col-lg-8">
                                                    <span className="fw-bold fs-6 text-gray-800">
                                                        {patient.insurance_card_number || '-'}
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                        <div className="row mb-7">
                                            <label className="col-lg-4 fw-semibold text-muted">Date of Birth</label>
                                            <div className="col-lg-8">
                                                <span className="fw-bold fs-6 text-gray-800">
                                                    {formatDate(patient.date_of_birth) || '-'} 
                                                    {patient.date_of_birth && <span className="text-muted fs-7 ms-2">({calculateAge(patient.date_of_birth)} years)</span>}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="row mb-7">
                                            <label className="col-lg-4 fw-semibold text-muted">Status</label>
                                            <div className="col-lg-8">
                                                {(() => {
                                                    const { badgeClass, label } = getGenericStatus(patient.status);
                                                    return <span className={`badge ${badgeClass}`}>{label}</span>;
                                                })()}
                                            </div>
                                        </div>
                                    </div>
                                </Tab>
                                
                                <Tab eventKey="appointments" title={`Appointments (${appointments.length})`}>
                                    <div className="py-5">
                                        <ul className="nav nav-tabs nav-line-tabs mb-5 fs-6">
                                            <li className="nav-item">
                                                <a 
                                                    className={`nav-link cursor-pointer ${appointmentType === 'doctor' ? 'active' : ''}`}
                                                    onClick={() => setAppointmentType('doctor')}
                                                >
                                                    Doctor Appointments
                                                </a>
                                            </li>
                                            <li className="nav-item">
                                                <a 
                                                    className={`nav-link cursor-pointer ${appointmentType === 'specialist' ? 'active' : ''}`}
                                                    onClick={() => setAppointmentType('specialist')}
                                                >
                                                    Specialist Appointments
                                                </a>
                                            </li>
                                        </ul>
                                        <div className="table-responsive">
                                            <table className="table align-middle table-row-dashed fs-6 gy-5">
                                                <thead>
                                                    <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0">
                                                        <th>Ref No</th>
                                                        <th>Date</th>
                                                        <th>{appointmentType === 'doctor' ? 'Doctor' : 'Specialist'}</th>
                                                        <th>Type</th>
                                                        <th>Status</th>
                                                        <th className="text-end">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {appointments
                                                        .filter(appt => appointmentType === 'doctor' ? !appt.specialist_id : appt.specialist_id)
                                                        .map((appt) => (
                                                        <tr key={appt.id}>
                                                            <td>
                                                                <span className="text-gray-800 fw-bold">{appt.ref_no || '-'}</span>
                                                            </td>
                                                            <td>
                                                                <div className="d-flex flex-column">
                                                                    <span className="text-gray-800 fw-bold">{formatDate(appt.date)}</span>
                                                                    <span className="text-muted fs-7">{appt.time}</span>
                                                                </div>
                                                            </td>
                                                            <td>{appointmentType === 'doctor' ? appt.doctor?.name : appt.specialist?.name}</td>
                                                            <td>{appt.checkup_type?.title}</td>
                                                            <td>
                                                                {(() => {
                                                                    const { badgeClass, label } = getAppointmentStatus(appt.status);
                                                                    return <span className={`badge ${badgeClass}`}>{label}</span>;
                                                                })()}
                                                            </td>
                                                            <td className="text-end">
                                                                <Link 
                                                                    to={appointmentType === 'specialist' ? `/specialist-checkup/${appt.id}` : `/appointments/${appt.id}/checkup`} 
                                                                    className="btn btn-sm btn-light btn-active-light-primary"
                                                                >
                                                                    View
                                                                </Link>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    {appointments.filter(appt => appointmentType === 'doctor' ? !appt.specialist_id : appt.specialist_id).length === 0 && (
                                                        <tr><td colSpan="6" className="text-center text-muted">No appointments found</td></tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </Tab>
                                
                                <Tab eventKey="orders" title={`Sales/Orders (${orders.length})`}>
                                    <div className="table-responsive py-5">
                                        <table className="table align-middle table-row-dashed fs-6 gy-5">
                                            <thead>
                                                <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0">
                                                    <th>Order ID</th>
                                                    <th>Ref No</th>
                                                    <th>Date</th>
                                                    <th>Items</th>
                                                    <th>Total</th>
                                                    <th>Status</th>
                                                    <th className="text-end">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {orders.map((order) => (
                                                    <tr key={order.id}>
                                                        <td>
                                                            <Link to={`/orders/${order.id}`} className="text-gray-800 text-hover-primary fw-bold">
                                                                #{order.id}
                                                            </Link>
                                                        </td>
                                                        <td>
                                                            <span className="text-gray-800 fw-bold">{order.ref_no || '-'}</span>
                                                        </td>
                                                        <td>{formatDate(order.created_at)}</td>
                                                        <td>{order.items_count || order.items?.length || 0} items</td>
                                                        <td>{formatCurrency(order.total_amount)}</td>
                                                        <td>
                                                            <span className={`badge badge-light-${order.status === 'completed' ? 'success' : order.status === 'pending' ? 'warning' : 'danger'}`}>
                                                                {order.status}
                                                            </span>
                                                        </td>
                                                        <td className="text-end">
                                                            <Link to={`/orders/${order.id}`} className="btn btn-sm btn-light btn-active-light-primary">
                                                                Details
                                                            </Link>
                                                        </td>
                                                    </tr>
                                                ))}
                                                {orders.length === 0 && (
                                                    <tr><td colSpan="7" className="text-center text-muted">No orders found</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </Tab>
                            </Tabs>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Details;
